import { AUTH_TOKEN } from 'constants/AuthConstant';

const USER_STORAGE_KEY = 'me';

/**
 * Servicio para manejar datos del usuario en localStorage
 * Abstrae la lógica de guardar, obtener y sincronizar datos del usuario
 */
const UserStorageService = {
	/**
	 * Guarda el usuario en localStorage
	 * @param {Object} user - Datos del usuario
	 */
	saveUser(user) {
		try {
			localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
		} catch (error) {
			console.error('Error saving user to localStorage:', error);
		}
	},

	/**
	 * Obtiene el usuario de localStorage
	 * @returns {Object|null} - Datos del usuario o null si no existe
	 */
	getUser() {
		try {
			const userData = localStorage.getItem(USER_STORAGE_KEY);
			return userData ? JSON.parse(userData) : null;
		} catch (error) {
			console.error('Error getting user from localStorage:', error);
			return null;
		}
	},

	/**
	 * Verifica si hay un usuario guardado
	 * @returns {boolean}
	 */
	hasUser() {
		return !!this.getUser();
	},

	/**
	 * Obtiene el token de autenticación
	 * @returns {string|null}
	 */
	getToken() {
		return localStorage.getItem(AUTH_TOKEN);
	},

	/**
	 * Verifica si hay un token válido
	 * @returns {boolean}
	 */
	hasValidToken() {
		return !!this.getToken();
	},

	/**
	 * Limpia todos los datos del usuario
	 */
	clearUser() {
		localStorage.removeItem(USER_STORAGE_KEY);
		localStorage.removeItem(AUTH_TOKEN);
	},

	/**
	 * Actualiza campos específicos del usuario sin sobrescribir todo
	 * @param {Object} updates - Campos a actualizar
	 */
	updateUser(updates) {
		const currentUser = this.getUser();
		if (currentUser) {
			const updatedUser = { ...currentUser, ...updates };
			this.saveUser(updatedUser);
			return updatedUser;
		}
		return null;
	},

	/**
	 * Verifica si los datos del usuario están desactualizados
	 * @param {number} maxAgeMinutes - Edad máxima en minutos (default: 60)
	 * @returns {boolean}
	 */
	isUserDataStale(maxAgeMinutes = 1) {
		const user = this.getUser();
		if (!user || !user.lastFetchTime) return true;

		const now = Date.now();
		const maxAge = maxAgeMinutes * 60 * 1000; // convertir a ms
		return now - user.lastFetchTime > maxAge;
	},

	/**
	 * Marca el tiempo de última actualización
	 */
	markAsRecentlyFetched(user) {
		return {
			...user,
			lastFetchTime: Date.now(),
		};
	},
};

export default UserStorageService;
